import clsx from 'clsx';
import type * as React from 'react';

import type { BlockHintData, BlockHintStyle } from '@gitbook/doc-core';
import type { Block } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';
import { Button } from '@gitbook/spine-react';

import { BlockWrapper } from '../common';
import { BlockVariantContext } from '../contexts';
import { HINT_STYLES } from './constants';
import { useHintBlockPaletteFactory } from './palette';

/**
 * Component to render a hint block.
 */
export function BlockHint(props: BlockProps): React.ReactElement {
    const { node, children, editor } = props;
    const { readOnly } = editor;
    const data = node.data.toFrozenObject<BlockHintData>();

    const hintStyle = HINT_STYLES[data.style];

    const onPressIcon = () => {
        // Iterate to the next Hint style
        const styles: BlockHintStyle[] = ['info', 'warning', 'danger', 'success'];
        const newStyle =
            data.style === styles[styles.length - 1]
                ? styles[0]
                : styles[styles.indexOf(data.style) + 1];

        editor.change((change) =>
            change.setNodeByKey(node.key, {
                data: {
                    style: newStyle,
                },
            })
        );
    };

    // Create command palette
    const palette = useHintBlockPaletteFactory({ nodeKey: node.key });

    // Get the first child to align the icon with it
    const firstChild = node.nodes.first() as Block;

    const hasHeading = ['heading-1', 'heading-2', 'heading-3'].includes(firstChild.type);

    const IconComponent = hintStyle.icon;

    return (
        <BlockWrapper {...props} palette={palette}>
            <div
                className={clsx(
                    'hint group relative rounded',
                    hasHeading ? 'border-l-2' : null,
                    styleClassNames[data.style].container
                )}
            >
                {/* Icon */}
                <div
                    className={clsx(
                        'absolute z-10',
                        readOnly ? 'mt-[calc(1rem-1px)] ml-4' : 'mt-2.5 ml-2',
                        styleClassNames[data.style].icon
                    )}
                >
                    {readOnly ? (
                        <IconComponent size={16} />
                    ) : (
                        <Button
                            onPress={onPressIcon}
                            iconOnly
                            kind="nested"
                            leadingIcon={<IconComponent size={16} />}
                            label="Change hint type"
                            className={styleClassNames[data.style].icon}
                        />
                    )}
                </div>

                {/* Content */}
                <div
                    className={clsx(
                        'relative [&>*]:pr-4',
                        readOnly ? '[&>*]:pl-10.75' : '[&>*]:pl-10.5',
                        hasHeading
                            ? [
                                  '[&>:first-child]:rounded-t-md',
                                  '[&>:first-child]:mb-3.5',
                                  'only:[&>:first-child]:mb-0',
                                  '[&>:first-child]:pb-3.5',
                                  '[&>:first-child_*]:font-bold',
                                  '[&>:first-child_*]:text-content-paragraph-small',
                                  styleClassNames[data.style].heading,
                              ]
                            : null,
                        styleClassNames[data.style].children
                    )}
                >
                    <BlockVariantContext.Provider value="hint">
                        {children}
                    </BlockVariantContext.Provider>
                </div>
            </div>
        </BlockWrapper>
    );
}

const styleClassNames = {
    info: {
        container: 'bg-info border-hint-info',
        icon: 'text-info',
        header: 'bg-info-standout',
        heading: '[&>:first-child]:bg-info-standout',
        children:
            '[&_[data-mention]]:text-info-standout [&_[data-mention]]:bg-info-standout [&_[data-link-broken=true]]:bg-info-standout',
    },
    warning: {
        container: 'bg-warning border-hint-warning',
        icon: 'text-warning',
        header: 'bg-warning-standout',
        heading: '[&>:first-child]:bg-warning-standout',
        children:
            '[&_a]:text-warning-standout [&_[data-mention]]:text-warning-standout [&_[data-mention]]:bg-warning-standout [&_[data-link-broken=true]]:bg-warning-standout',
    },
    danger: {
        container: 'bg-danger border-hint-danger',
        icon: 'text-danger',
        header: 'bg-danger-standout',
        heading: '[&>:first-child]:bg-danger-standout',
        children:
            '[&_a]:text-danger-standout [&_[data-mention]]:text-danger-standout [&_[data-mention]]:bg-danger-standout [&_[data-link-broken=true]]:bg-danger-standout',
    },
    success: {
        container: 'bg-success border-hint-success',
        icon: 'text-success',
        header: 'bg-success-standout',
        heading: '[&>:first-child]:bg-success-standout',
        children:
            '[&_a]:text-success-standout [&_[data-mention]]:text-success-standout [&_[data-mention]]:bg-success-standout [&_[data-link-broken=true]]:bg-success-standout',
    },
};
